<template>
  <article
    class="car-card group rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm hover:shadow-md transition"
  >
    <!-- Top row: image + main info + action -->
    <div class="grid grid-cols-1 gap-6 md:grid-cols-[minmax(240px,340px),1fr,auto] items-start">
      <!-- Image -->
      <div class="w-full flex items-center justify-center bg-slate-50/50 rounded-xl overflow-hidden h-[140px] max-h-[160px]">
        <img
          class="w-full h-full object-contain p-2"
          :src="carr.image && carr.image !== '' && !carr.image.includes('default.jpg') ? carr.image : getCategoryImage(carr.type?.name)"
          alt="Vehicle image"
        />
      </div>

      <!-- Title, price / badge, quick specs -->
      <div class="flex flex-col gap-4">
        <!-- Title + type -->
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
          <div class="flex flex-col">
            <h3 class="text-xl font-bold text-slate-900 leading-tight">
              {{ (Number(carr?.price || 0) <= 0 || (isLead && isLead == '1')) ? (carr?.type?.name || carr?.name) : carr?.name }}
            </h3>
            <p v-if="Number(carr?.price || 0) > 0" class="text-sm font-medium text-slate-500 uppercase tracking-wide">
               {{ carr?.type?.name }}
            </p>
          </div>

          <!-- Price OR On request -->
          <div v-if="hasPrice" class="text-right">
            <!-- total -->
            <div v-if="carr.isCalculating" class="text-lg font-bold text-slate-900 flex items-center gap-2">
               <span class="text-sm font-normal animate-pulse">Calculating Price...</span>
            </div>
            <div v-else class="text-lg font-bold text-slate-900">
              {{ formattedTotal }}
            </div>
            <!-- context + daily rate when hourly -->
            <div v-if="userSearch.type === 'hourly'" class="mt-0.5 text-xs text-slate-500">
              Price for <span class="font-semibold">{{ daysLabel }}</span>
            </div>
            <div v-if="userSearch.type === 'hourly'" class="text-[13px] text-slate-700">
              <span class="font-medium">Daily rate:</span>
              <span class="font-semibold">{{ formattedDaily }}</span>
            </div>
          </div>
          <span
            v-else
            class="inline-flex w-max items-center rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800"
          >
            On request
          </span>
        </div>

        <!-- Quick specs -->
        <div class="flex flex-wrap items-center gap-4 text-sm text-slate-600">
          <div class="inline-flex items-center gap-2">
            <span class="font-icon theme-icon users-icon text-[22px] md:text-[22px] text-slate-800 leading-none"> </span>
            <span class="inline-flex h-7 min-w-[32px] items-center justify-center rounded-full border border-slate-200 px-2">
              {{ carr?.type?.passengers ?? '—' }}
            </span>
            <span class="sr-only">Passengers</span>
          </div>

          <div class="inline-flex items-center gap-2">
            <span class="font-icon theme-icon suitcase-icon text-[22px] md:text-[22px] text-slate-800 leading-none"> </span>
            <span class="inline-flex h-7 min-w-[32px] items-center justify-center rounded-full border border-slate-200 px-2">
              {{ carr?.type?.bags ?? '—' }}
            </span>
            <span class="sr-only">Bags</span>
          </div>

          <div v-if="carr?.type?.doors != null" class="inline-flex items-center gap-2">
            <span class="text-[18px]">🚪</span>
            <span class="inline-flex h-7 min-w-[32px] items-center justify-center rounded-full border border-slate-200 px-2">
              {{ carr?.type?.doors ?? '—' }}
            </span>
            <span class="sr-only">Doors</span>
          </div>
        </div>
      </div>

      <!-- Select / Selected -->
      <div v-if="canSelect" class="mt-1 md:mt-0">
        <button
          v-if="selectedCar && carr && selectedCar.id === carr.id"
          class="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-emerald-600 bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white md:w-auto"
          disabled
        >
          Select
          <font-awesome-icon size="lg" icon="fa-solid fa-check" class="text-white" />
        </button>

        <button
          v-else
          @click="handleSelectCar(carr)"
          class="inline-flex w-full items-center justify-center rounded-full border-2 border-slate-900 bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-white hover:text-slate-900 md:w-auto"
        >
          Select
        </button>
      </div>
    </div>

    <!-- Footer: More info toggle -->
    <div class="mt-5 flex items-center justify-between border-t border-slate-200 pt-4">
      <button
        @click="expanded = !expanded"
        class="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-600 hover:text-slate-900"
      >
        <span
          class="grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white transition group-hover:bg-slate-100"
        >
          <font-awesome-icon
            size="xs"
            icon="fa-solid fa-chevron-down"
            class="transition"
            :class="expanded ? 'rotate-180 text-slate-900' : 'text-slate-600'"
          />
        </span>
        More info
      </button>

      <!-- (Optional) small policy hint -->
      <div class="text-xs text-slate-500">
        Free cancellation up to <span class="font-medium">72h before pickup</span>
      </div>
    </div>

    <!-- Expandable body -->
    <transition name="fade-slide">
      <div v-show="expanded" class="mt-4 rounded-xl bg-slate-50 p-4">
        <div
          class="text-[15px] leading-relaxed text-slate-700"
          v-html="(carr?.type?.description || '').replace(/\n/g, '<br>')"
        ></div>

        <div class="mt-4 grid grid-cols-1 gap-3 rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div class="flex items-center gap-3">
            <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Cancellation</div>
            <div class="ml-auto text-sm text-slate-700 text-right">
              Free cancellation up to 72 hours before pickup
            </div>
          </div>
          <!-- Extra Charges Section -->
          <div v-if="extraKmPrice > 0 || extraHrPrice > 0" class="mt-2 pt-2 border-t border-slate-100 flex flex-col gap-2">
            <div v-if="extraKmPrice > 0" class="flex items-center gap-3">
              <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Extra KM</div>
              <div class="ml-auto text-sm text-slate-700 font-medium">
                ${{ extraKmPrice.toFixed(2) }} per km
              </div>
            </div>
            <div v-if="extraHrPrice > 0" class="flex items-center gap-3">
              <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Extra Hour</div>
              <div class="ml-auto text-sm text-slate-700 font-medium">
                ${{ extraHrPrice.toFixed(2) }} per hour
              </div>
            </div>
            <div class="text-[11px] text-slate-400 italic">
              * Additional charges will be collected at the end of service if limits are exceeded.
            </div>
          </div>
        </div>
      </div>
    </transition>
  </article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Car as CarData, UserSearch } from '@/types'
import { retrieveUserSearch } from '@/services/search-service'
import { getHoursPerDay } from '@/utils/common'
import { calculateCarPrice } from '@/utils/pricing'

// Category images mapping
import sClassImg from '@/assets/cars/luxury-sedan.png'
import vClassImg from '@/assets/cars/van.png'
import eClassImg from '@/assets/cars/premium-sedan.png'
import gmcSuvImg from '@/assets/cars/suv.png'

const getCategoryImage = (typeName: string) => {
  const name = (typeName || '').toLowerCase()
  if (name.includes('premium')) return eClassImg
  if (name.includes('luxury')) return sClassImg
  if (name.includes('van')) return vClassImg
  if (name.includes('suv')) return gmcSuvImg
  if (name.includes('bus')) return '/assets/busescoaches.jpeg'
  if (name.includes('boat')) return '/assets/nocarfound.jpeg'
  if (name.includes('standard')) return eClassImg
  
  return '/assets/nocarfound.jpeg'
}

const props = defineProps<{
  canSelect?: boolean
  car: any
  selectedCar?: any
  isLead?: string | number
  kmDistance?: number
}>()

const emit = defineEmits<{
  (e: 'selected', car: CarData): void
}>()

const userSearch = ref<UserSearch>(retrieveUserSearch())
const expanded = ref(false)

// hours → days (for hourly)
const hoursPerDay = getHoursPerDay()
const days = computed(() => {
  const h = Number(userSearch.value?.hours ?? 0)
  return h > 0 ? h / (hoursPerDay || 10) : 0
})
const daysLabel = computed(() => {
  let label = `${days.value}`.replace('.0', '') + ' day(s)'
  const locHours = props.car?.chaufferLocation?.hours_per_day
  const supplierHours = props.car?.supplier?.hours
  const locKm = props.car?.chaufferLocation?.km_per_day
  
  const finalH = (locHours && Number(locHours) > 0) ? locHours : supplierHours
  
  if (finalH && Number(finalH) > 0) {
    label += ` (${Number(finalH).toFixed(0)} hrs`
    if (locKm && Number(locKm) > 0) {
      label += ` & ${Number(locKm).toFixed(0)} km`
    }
    label += `)`
  }
  return label
})

// ===== Pricing-calculated car snapshot (keeps your original rules) =====
const carr = computed(() => {
  const pricing = calculateCarPrice(props.car, userSearch.value, props.kmDistance)
  return {
    ...(props.car || {}),
    ...pricing
  }
})

// Extra Charges Calculation
const extraKmPrice = computed(() => {
  let charge = parseFloat(props.car?.chaufferLocation?.extra_km_charge || props.car?.transferLocation?.extra_km_charge || '0')
  if (charge > 0 && props.car?.priceFactor > 0) {
    charge = charge / ((100 - props.car.priceFactor) / 100)
  }
  return charge
})

const extraHrPrice = computed(() => {
  let charge = parseFloat(props.car?.chaufferLocation?.extra_hr_charge || props.car?.transferLocation?.extra_hr_charge || '0')
  if (charge > 0 && props.car?.priceFactor > 0) {
    charge = charge / ((100 - props.car.priceFactor) / 100)
  }
  return charge
})

const hasPrice = computed(() => Number(carr.value?.price || 0) > 0)
const formattedTotal = computed(() => {
  const v = Number(carr.value?.price || 0)
  return v ? `$${v.toFixed(2)}` : ''
})
const formattedDaily = computed(() => {
  const v = Number(carr.value?.dailyrate || 0)
  return v ? `$${v.toFixed(2)}` : '$0.00'
})

const handleSelectCar = (car: CarData) => {
  emit('selected', car)
}
</script>

<style scoped>
/* simple appear/leave for the details area */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all .22s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>